import type { OperationRun } from '@foodlista/trigger-vue'
import type { OperationItem } from '../types'
import type { OperationStatus } from '../utils/lifecycle'

export interface OperationPresentationLink {
  /** Unlocalized app path; the row applies `localePath`. */
  path: string
  labelKey: string
}

export interface OperationPresentation {
  subtitle?: string
  link?: OperationPresentationLink
}

export interface OperationPresenterContext {
  item: OperationItem
  run: OperationRun | null
  status: OperationStatus
  t: (
    key: string,
    named?: Record<string, unknown>,
    plural?: number,
  ) => string
}

/**
 * FE-side summary for a finished operation: the line the BOC row shows once
 * the run is no longer emitting progress, plus an optional deep link to the
 * entity the operation touched.
 *
 * A presenter reads only its own operation's typed context (and, when the
 * summary needs it, the run output). It must fail soft — unparseable context
 * or output returns `undefined` so the row falls back to the status string.
 */
export type OperationPresenter = (
  ctx: OperationPresenterContext,
) => OperationPresentation | undefined

const presenters = new Map<string, OperationPresenter>()

export function registerOperationPresenter(
  operationType: string,
  presenter: OperationPresenter,
) {
  presenters.set(operationType, presenter)
}

export function getOperationPresenter(
  operationType: string,
): OperationPresenter | undefined {
  return presenters.get(operationType)
}
